"use no memo";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import classNames from "classnames";
import { TableVirtuoso } from "react-virtuoso";
import {
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";

import { RootState } from "src/redux/reducers";
import { useTableSearch, TableSearchBar } from "src/app/components/TableSearch";
import { getMetaValues } from "src/app/components/TableVirtuosoSearch/functions";
import {
  setActiveSortingColumn,
  setLocalSyncToggle,
  setSelectedFilters,
  setSelectedTasks,
  setSortingDirection,
  setSelectedTaskColumns,
} from "src/redux";
import { loadLocalProjectSettings } from "./tasksTableSorting/localStorageManagement/loadLocalProjectSettings";
import { saveLocalProjectSettings } from "./tasksTableSorting/localStorageManagement/saveLocalProjectSettings";
import { ifRowMatchesFilters } from "./tasksTableSorting/tableSortingFunctions/filterTableData";
import { synchronizeLocalFilters } from "./tasksTableSorting/tableSortingFunctions/synchronizeLocalFilters";
import {
  VirtuosoTable,
  VirtuosoTableBody,
  VirtuosoTableHead,
  VirtuosoTableRow,
  TableVirtuosoContext,
} from "./components/virtuosoComponents";
import { TableTaskData } from "./types";
import { SingleProjectSelectedColumnsBar } from "../components/SingleProjectSelectedColumnsBar";
import useDragAndDrop from "./hooks/useDragAndDrop";

import "./TasksTable.scss";

interface TasksTableProps {
  columns: any;
  selectedRowsTotalRow?: Record<string, any>;
}

export default function TasksTable(props: TasksTableProps) {
  const { columns, selectedRowsTotalRow } = props;

  const [data, setData] = useState<TableTaskData[]>([]);
  const [draggableColumns, setDraggableColumns] = useState(
    columns.filter((column: any) => column.columnFromApi),
  );
  const [rowSelection, setRowSelection] = useState({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isDragging, setIsDragging] = useState(false);

  const dispatch = useDispatch();

  const virtuoso = useRef<any>(null);

  const params = useParams<{ projectId: string }>();

  const { projectId } = params;

  const identity = useSelector(
    (state: RootState) => state.mainReducer.identity,
  );

  const tasks = useSelector((state: RootState) => state.taskReducer.tasks);

  const tasksTotalCount = useSelector(
    (state: RootState) => state.taskReducer.tasksTotalCount,
  );

  const tasksLoading = useSelector(
    (state: RootState) => state.taskReducer.tasksLoading,
  );

  const taskType = useSelector(
    (state: RootState) => state.projectReducer.taskType,
  );

  const activeSortingColumn = useSelector(
    (state: RootState) => state.taskFiltersReducer.activeSortingColumn,
  );

  const sortingDirection = useSelector(
    (state: RootState) => state.taskFiltersReducer.sortingDirection,
  );

  const selectedFilters = useSelector(
    (state: RootState) => state.taskFiltersReducer.selectedFilters,
  );

  const localSyncToggle = useSelector(
    (state: RootState) => state.taskFiltersReducer.localSyncToggle,
  );

  const tableInputFocused = useSelector(
    (state: RootState) => state.taskReducer.tableInputFocused,
  );

  const selectedTasks = useSelector(
    (state: RootState) => state.taskReducer.selectedTasks,
  );

  const membersList = useSelector(
    (state: RootState) => state.projectReducer.membersList,
  );

  const dictionaryAutoCompletes = useSelector(
    (state: RootState) => state.dictionaryReducer.dictionaryAutoCompletes,
  );

  const projectTasksAutocomplete = useSelector(
    (state: RootState) => state.taskReducer.projectTasksAutocomplete,
  );

  const wsProjectAutocompleteList = useSelector(
    (state: RootState) => state.projectReducer.wsProjectAutocompleteList,
  );

  const selectedTaskColumns = useSelector(
    (state: RootState) => state.taskReducer.selectedTaskColumns,
  );

  const intl = useIntl();

  const table = useReactTable({
    data,
    columns,
    debugTable: true,
    state: {
      rowSelection,
      sorting,
      columnPinning: {
        left: [
          "drag-handle",
          "select",
          "creator",
          "content",
          "publication",
          "payment",
          "action",
        ],
        right: [" action"],
      },
    },
    enableColumnResizing: true,
    enableSorting: true,
    enableRowSelection: true,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    getRowId: (row: { id: string }) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const { rows } = table.getRowModel();

  const {
    handleColumnDragEnd,
    handleRowDragEnd,
    moveColumnsToTheLeftEnd,
    moveColumnsToTheRightEnd,
  } = useDragAndDrop({
    rows,
    setData,
    setIsDragging,
    draggableColumns,
    projectId,
  });

  useEffect(() => {
    if (isDragging || tableInputFocused) return;
    const tableData = tasks
      .filter((task) => {
        return ifRowMatchesFilters(task, selectedFilters, identity.id);
      })
      .map((el) => ({
        id: el.taskId, // id === taskId (react-table needs id)
        ...el,
        publication: null,
        publicationCopy: el.publication ?? null, // This is copied because cell.render() method tries render publication this object
      }))
      .sort((a, b) => b.rank?.localeCompare(a.rank));
    setData(tableData);
  }, [tasks, selectedFilters, identity.id, isDragging, tableInputFocused]);

  useEffect(() => {
    return () => {
      dispatch(setActiveSortingColumn(null));
      dispatch(setSelectedFilters({}));
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(setActiveSortingColumn(null));
    dispatch(setSelectedFilters({}));

    if (projectId) {
      loadLocalProjectSettings(projectId, taskType, dispatch);
    }
  }, [dispatch, taskType, projectId]);

  useEffect(() => {
    saveLocalProjectSettings(
      projectId,
      activeSortingColumn,
      taskType,
      sortingDirection,
      selectedFilters,
    );
  }, [
    activeSortingColumn,
    sortingDirection,
    selectedFilters,
    projectId,
    taskType,
  ]); // saves sort data to local storage

  useEffect(() => {
    if (localSyncToggle) {
      synchronizeLocalFilters(
        data,
        projectId,
        taskType,
        setSelectedFilters,
        setLocalSyncToggle,
        dispatch,
      );
    }
  }, [dispatch, data, projectId, taskType, localSyncToggle]);

  useEffect(() => {
    if (columns.length) {
      setDraggableColumns(
        columns.filter((column: any) => column.columnFromApi),
      );
    } else {
      setDraggableColumns([]);
    }
  }, [columns]);

  // --- Table search ---

  const cellTextRows = useMemo(
    () =>
      getMetaValues(
        // @ts-ignore
        rows.map((item) => item.original),
        columns.map((column: { id: string }) => column.id),
        membersList,
        dictionaryAutoCompletes,
        intl,
        projectId,
        projectTasksAutocomplete,
        wsProjectAutocompleteList,
      ),
    [
      taskType,
      rows,
      columns,
      membersList,
      dictionaryAutoCompletes,
      intl,
      projectId,
      projectTasksAutocomplete,
      wsProjectAutocompleteList,
    ],
  );

  const handleScrollToResult = useCallback(
    (result: { rowIndex: number; columnIndex: number }) => {
      const { rowIndex, columnIndex } = result;
      if (typeof rowIndex !== "number" || !virtuoso.current) return;

      // +2 offset: drag-handle + select columns precede data columns
      const COLUMN_OFFSET = 2;
      const headers = document.querySelectorAll(".tasks-table th");
      const header = headers[columnIndex + COLUMN_OFFSET] as HTMLElement;

      header?.scrollIntoView({ block: "nearest", inline: "center" });
      virtuoso.current?.scrollIntoView({ index: rowIndex, align: "center" });
    },
    [],
  );

  const tableSearch = useTableSearch({
    cellTextRows,
    onScrollToResult: handleScrollToResult,
  });

  // Clear search when taskType changes
  useEffect(() => {
    tableSearch.close();
  }, [taskType]);

  useEffect(() => {
    const selectedRowsIds = Object.keys(rowSelection);

    const newSelectedRows = rows
      .filter((row) => selectedRowsIds.includes(row.id))
      .map((row) => row.original);
    dispatch(setSelectedTasks(newSelectedRows));
  }, [dispatch, rows, rowSelection]);

  useEffect(() => {
    if (!selectedTasks.length && Object.keys(rowSelection).length) {
      setRowSelection({});
    }

    if (selectedTasks.length > 0) {
      dispatch(setSelectedTaskColumns([]));
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedTasks]);

  useEffect(() => {
    if (selectedTaskColumns.length > 0) {
      dispatch(setSelectedTasks([]));
    }
  }, [dispatch, selectedTaskColumns]);

  useEffect(() => {
    if (activeSortingColumn === "") {
      setSorting([]);
      dispatch(setSortingDirection("sortingDescending"));
    } else {
      const columnHeaders = table.getHeaderGroups()[0].headers;
      const activeSortingColumnInstance = columnHeaders.find(
        (col) => col.id === activeSortingColumn,
      );
      const ascending = sortingDirection === "sortingAscending";
      if (activeSortingColumnInstance) {
        // @ts-ignore
        activeSortingColumnInstance.column.getToggleSortingHandler();
      }

      // @ts-ignore
      setSorting([{ id: activeSortingColumn, desc: !ascending }]);
    }
  }, [dispatch, table, activeSortingColumn, sortingDirection]); // connect react-table sorting to reducer state

  return (
    <>
      <TableSearchBar search={tableSearch} />

      <div
        className={classNames("tasks-table", "tasks-table--sticky", {
          "tasks-table--dragging": isDragging,
        })}
      >
        <TableVirtuoso
          ref={virtuoso}
          data={rows}
          context={
            {
              table,
              handleRowDragEnd,
              handleColumnDragEnd,
              setIsDragging,
              tasksLoading,
              totalRowCount: tasksTotalCount,
              highlightedCellKeys: tableSearch.highlightedCellKeys,
              activeResult: tableSearch.activeResult,
              selectedRowIds: Object.keys(rowSelection),
              selectedRowsTotalRow,
            } satisfies TableVirtuosoContext
          }
          totalCount={rows.length}
          customScrollParent={
            (document.querySelector(".projects") as HTMLElement) || undefined
          }
          components={{
            Table: VirtuosoTable,
            // @ts-ignore
            TableHead: VirtuosoTableHead,
            // @ts-ignore
            TableBody: VirtuosoTableBody,
            TableRow: VirtuosoTableRow,
          }}
          fixedHeaderContent={() => <></>}
        />
      </div>

      {selectedTaskColumns.length > 0 && (
        <SingleProjectSelectedColumnsBar
          moveColumnsToTheLeftEnd={moveColumnsToTheLeftEnd}
          moveColumnsToTheRightEnd={moveColumnsToTheRightEnd}
        />
      )}
    </>
  );
}
